/*
 * <dwm-team-story> — a team member's personal story in a modal dialog (sections/dwm-team.liquid, layout "Profielen").
 * Only rendered when the member has a personal text.
 *
 * The picture is a real <button> (Enter / Space / click / tap) that opens a native <dialog> with showModal(): the rest
 * of the page becomes inert and Escape closes it. On top of that:
 * - focus moves to the close button on open and is kept inside the dialog (Tab / Shift+Tab wrap around);
 * - a click on the backdrop or the close button closes it;
 * - the page behind it does not scroll (class on <html>, the scrollbar width is kept so nothing shifts);
 * - on close, focus returns to the picture that opened it.
 * Without this script the button stays disabled and shows no CTA: the profile is fully visible, only the dialog is
 * missing.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
let openDialogs = 0;

function lockScroll(lock) {
  const root = document.documentElement;
  openDialogs = Math.max(0, openDialogs + (lock ? 1 : -1));

  if (openDialogs > 0) {
    if (!root.classList.contains('dwm-dialog-lock')) {
      root.style.setProperty('--dwm-scrollbar-width', `${window.innerWidth - root.clientWidth}px`);
      root.classList.add('dwm-dialog-lock');
    }
  } else {
    root.classList.remove('dwm-dialog-lock');
    root.style.removeProperty('--dwm-scrollbar-width');
  }
}

class DwmTeamStory extends HTMLElement {
  #trigger = null;
  #dialog = null;
  #pointerDownOnBackdrop = false;

  connectedCallback() {
    this.#trigger = this.querySelector('[data-dwm-team-open]');
    this.#dialog = this.querySelector('dialog');

    if (!this.#trigger || !this.#dialog || typeof this.#dialog.showModal !== 'function') {
      return;
    }

    this.#trigger.disabled = false;
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.classList.add('is-ready');
    this.#trigger.addEventListener('click', this.open);
    this.#dialog.addEventListener('pointerdown', this.#onPointerDown);
    this.#dialog.addEventListener('click', this.#onClick);
    this.#dialog.addEventListener('keydown', this.#onKeydown);
    this.#dialog.addEventListener('close', this.#onClose);
  }

  disconnectedCallback() {
    if (this.#dialog?.open) {
      this.#dialog.close();
    }
    this.#trigger?.removeEventListener('click', this.open);
    this.#dialog?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#dialog?.removeEventListener('click', this.#onClick);
    this.#dialog?.removeEventListener('keydown', this.#onKeydown);
    this.#dialog?.removeEventListener('close', this.#onClose);
  }

  open = () => {
    if (this.#dialog.open) {
      return;
    }
    lockScroll(true);
    this.#dialog.showModal();
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#dialog.querySelector('[data-dwm-team-close]')?.focus();
  };

  close = () => {
    if (this.#dialog.open) {
      this.#dialog.close();
    }
  };

  // The dialog box has no padding and is filled by the close button and the panel, so a pointer event whose target is
  // the <dialog> itself happened on the backdrop. Both press and release must be there (no close after a text drag).
  #onPointerDown = (event) => {
    this.#pointerDownOnBackdrop = event.target === this.#dialog;
  };

  #onClick = (event) => {
    if (event.target.closest('[data-dwm-team-close]')) {
      this.close();
    } else if (event.target === this.#dialog && this.#pointerDownOnBackdrop) {
      this.close();
    }
    this.#pointerDownOnBackdrop = false;
  };

  #onKeydown = (event) => {
    if (event.key !== 'Tab') {
      return;
    }
    const items = [...this.#dialog.querySelectorAll(FOCUSABLE)].filter((item) => item.getClientRects().length > 0);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.#dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.#dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  // Runs for every way of closing: close button, backdrop, Escape.
  #onClose = () => {
    lockScroll(false);
    this.#trigger.setAttribute('aria-expanded', 'false');
    if (this.#trigger.isConnected) {
      this.#trigger.focus({ preventScroll: true });
    }
  };
}

if (!customElements.get('dwm-team-story')) {
  customElements.define('dwm-team-story', DwmTeamStory);
}
